import React, { useState, useEffect, useCallback } from 'react'
import {
  DashboardhMediumButton,
  Icon,
} from 'bd-web-components'
import {
  PRIMARY_RED_01,
} from 'bd-web-components/src/utils/dash-colors'
import { useSpotify } from '../../../../hooks/spotify'
import {
  FormContainer,
  FormTitle,
  FormSubtitle,
  FormField,
  FormLabel,
  FormSelect,
  FormOption,
  ArtistButton,
  ArtistButtonText,
  ArtistModalOverlay,
  ArtistModalContent,
  ArtistModalHeader,
  ArtistModalTitle,
  ArtistModalClose,
  ArtistSearchInput,
  ArtistList,
  ArtistItem,
  ArtistName,
  ArtistType,
  SelectedArtists,
  SelectedArtistItem,
  SelectedArtistName,
  RemoveArtistButton,
} from './styles'
import { getDashColor } from '../../../../styles'

interface Artist {
  artistId: string
  artistName: string
  spotifyArtistId: string
  actType: 'HEADLINER' | 'SUPPORT'
  thumbnail?: string
}

interface SpotifyFormProps {
  onSubmit: (data: {
    event_category: 'CONCERT' | 'FESTIVAL'
    artists: Artist[]
  }) => void
  initialData?: {
    event_category: 'CONCERT' | 'FESTIVAL'
    artists: Artist[]
  }
  isEditing?: boolean
  onCancel?: () => void
}

export function SpotifyForm({ onSubmit, initialData, isEditing = false, onCancel }: SpotifyFormProps) {
  const [eventCategory, setEventCategory] = useState<'CONCERT' | 'FESTIVAL'>(initialData?.event_category || 'CONCERT')
  const [showArtistModal, setShowArtistModal] = useState(false)
  const [showSupportingArtistModal, setShowSupportingArtistModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [supportingSearchTerm, setSupportingSearchTerm] = useState('')
  const [selectedArtists, setSelectedArtists] = useState<Artist[]>(initialData?.artists.filter(artist => artist.actType === 'HEADLINER') || [])
  const [selectedSupportingArtists, setSelectedSupportingArtists] = useState<Artist[]>(initialData?.artists.filter(artist => artist.actType === 'SUPPORT') || [])
  const [artists, setArtists] = useState<Artist[]>([])
  const [supportingArtists, setSupportingArtists] = useState<Artist[]>([])
  const [isLoadingArtists, setIsLoadingArtists] = useState(false)
  const [isLoadingSupportingArtists, setIsLoadingSupportingArtists] = useState(false)

  const { getArtists } = useSpotify()

  // Função para buscar artistas
  const searchArtists = useCallback(async (name: string) => {
    if (name.length < 2) return

    try {
      setIsLoadingArtists(true)
      const response = await getArtists(name)
      console.log(response)
      // Converter resposta da API para o formato esperado
      const formattedArtists: Artist[] = response.artists.map(artist => ({
        artistId: artist.id,
        artistName: artist.name,
        spotifyArtistId: artist.id, // Usando o mesmo ID como spotify_id
        actType: 'HEADLINER' as const,
        thumbnail: artist.image // Usando o campo 'image' da API
      }))

      setArtists(formattedArtists)
    } catch (error) {
      console.error('Erro ao buscar artistas:', error)
    } finally {
      setIsLoadingArtists(false)
    }
  }, [getArtists])

  // Função para buscar artistas de apoio
  const searchSupportingArtists = useCallback(async (name: string) => {
    if (name.length < 2) return

    try {
      setIsLoadingSupportingArtists(true)
      const response = await getArtists(name)
      console.log(response)
      // Converter resposta da API para o formato esperado
      const formattedArtists: Artist[] = response.artists.map(artist => ({
        artistId: artist.id,
        artistName: artist.name,
        spotifyArtistId: artist.id, // Usando o mesmo ID como spotify_id
        actType: 'SUPPORT' as const,
        thumbnail: artist.image // Usando o campo 'image' da API
      }))

      setSupportingArtists(formattedArtists)
    } catch (error) {
      console.error('Erro ao buscar artistas de apoio:', error)
    } finally {
      setIsLoadingSupportingArtists(false)
    }
  }, [getArtists])

  // Buscar artistas quando o termo de busca mudar
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchArtists(searchTerm)
      } else {
        setArtists([])
      }
    }, 300) // Debounce de 300ms

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  // Buscar artistas de apoio quando o termo de busca mudar
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (supportingSearchTerm) {
        searchSupportingArtists(supportingSearchTerm)
      } else {
        setSupportingArtists([])
      }
    }, 300) // Debounce de 300ms

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportingSearchTerm])

  const filteredArtists = artists.filter(artist =>
    artist.artistName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredSupportingArtists = supportingArtists.filter(artist =>
    artist.artistName.toLowerCase().includes(supportingSearchTerm.toLowerCase())
  )

  const handleArtistSelect = (artist: Artist) => {
    if (!selectedArtists.find(a => a.artistId === artist.artistId)) {
      setSelectedArtists([...selectedArtists, artist])
    }
    setSearchTerm('')
    setShowArtistModal(false)
  }

  const handleSupportingArtistSelect = (artist: Artist) => {
    if (!selectedSupportingArtists.find(a => a.artistId === artist.artistId)) {
      setSelectedSupportingArtists([...selectedSupportingArtists, artist])
    }
    setSupportingSearchTerm('')
    setShowSupportingArtistModal(false)
  }

  const handleRemoveArtist = (artistId: string) => {
    setSelectedArtists(selectedArtists.filter(a => a.artistId !== artistId))
  }

  const handleRemoveSupportingArtist = (artistId: string) => {
    setSelectedSupportingArtists(selectedSupportingArtists.filter(a => a.artistId !== artistId))
  }

  const handleSubmit = () => {
    if (selectedArtists.length > 0) {
      onSubmit({
        event_category: eventCategory,
        artists: [...selectedArtists, ...selectedSupportingArtists]
      })
    }
  }

  return (
    <>
      <FormContainer>
        <FormTitle>{isEditing ? 'Editar configuração do Spotify' : 'Configurar Spotify para este evento'}</FormTitle>
        <FormSubtitle>
          {isEditing
            ? 'Atualize as informações do Spotify para este evento'
            : 'Preencha as informações necessárias para integrar este evento com o Spotify'
          }
        </FormSubtitle>

        <FormField>
          <FormLabel>Categoria do Evento</FormLabel>
          <FormSelect
            value={eventCategory}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEventCategory(e.target.value as 'CONCERT' | 'FESTIVAL')}
          >
            <FormOption value="CONCERT">Concerto</FormOption>
            <FormOption value="FESTIVAL">Festival</FormOption>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel>Artistas</FormLabel>
          <ArtistButton onClick={() => setShowArtistModal(true)}>
            <Icon
              fill={PRIMARY_RED_01}
              classes=""
              iconName="music"
              width={16}
              height={16}
            />
            <ArtistButtonText>Adicionar Artistas</ArtistButtonText>
          </ArtistButton>

          {selectedArtists.length > 0 && (
            <SelectedArtists>
              {selectedArtists.map((artist) => (
                <SelectedArtistItem key={artist.artistId}>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {artist.thumbnail && (
                      <img
                        src={artist.thumbnail}
                        alt={artist.artistName}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginRight: '8px'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    )}
                    <SelectedArtistName>{artist.artistName}</SelectedArtistName>
                  </div>
                  <RemoveArtistButton onClick={() => handleRemoveArtist(artist.artistId)}>
                    <Icon
                      width={20}
                      height={20}
                      iconName={'x'}
                      fill={PRIMARY_RED_01}
                      classes="add-account-chevron"
                    />
                  </RemoveArtistButton>
                </SelectedArtistItem>
              ))}
            </SelectedArtists>
          )}
        </FormField>

        <FormField>
          <FormLabel>Apoios ao Artista</FormLabel>
          <ArtistButton onClick={() => setShowSupportingArtistModal(true)}>
            <Icon
              fill={PRIMARY_RED_01}
              classes=""
              iconName="music"
              width={16}
              height={16}
            />
            <ArtistButtonText>Adicionar Apoios</ArtistButtonText>
          </ArtistButton>

          {selectedSupportingArtists.length > 0 && (
            <SelectedArtists>
              {selectedSupportingArtists.map((artist) => (
                <SelectedArtistItem key={artist.artistId}>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {artist.thumbnail && (
                      <img
                        src={artist.thumbnail}
                        alt={artist.artistName}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginRight: '8px'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    )}
                    <SelectedArtistName>{artist.artistName}</SelectedArtistName>
                  </div>
                  <RemoveArtistButton onClick={() => handleRemoveSupportingArtist(artist.artistId)}>
                    <Icon
                      width={20}
                      height={20}
                      iconName={'x'}
                      fill={PRIMARY_RED_01}
                      classes="add-account-chevron"
                    />
                  </RemoveArtistButton>
                </SelectedArtistItem>
              ))}
            </SelectedArtists>
          )}
        </FormField>

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <DashboardhMediumButton
            onClick={handleSubmit}
            color="#fff"
            background={getDashColor('SECONDARY_BLUE_01', 'GRAY_DARK')}
            height="48px"
            maxWidth="100%"
            variant="contained"
            width="204px"
            disabled={selectedArtists.length === 0}
          >
            {isEditing ? 'Atualizar Spotify' : 'Configurar Spotify'}
          </DashboardhMediumButton>

          {isEditing && onCancel && (
            <DashboardhMediumButton
              onClick={onCancel}
              variant="outlined"
            >
              Cancelar
            </DashboardhMediumButton>
          )}
        </div>
      </FormContainer>

      {showArtistModal && (
        <ArtistModalOverlay onClick={() => setShowArtistModal(false)}>
          <ArtistModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ArtistModalHeader>
              <ArtistModalTitle>Selecionar Artistas</ArtistModalTitle>
              <ArtistModalClose onClick={() => setShowArtistModal(false)}>
                <Icon
                  width={20}
                  height={20}
                  iconName={'x'}
                  fill={PRIMARY_RED_01}
                  classes="add-account-chevron"
                />
              </ArtistModalClose>
            </ArtistModalHeader>

            <ArtistSearchInput
              type="text"
              placeholder="Buscar artistas..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />

            <ArtistList>
              {isLoadingArtists ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Buscando artistas...
                </div>
              ) : filteredArtists.length > 0 ? (
                filteredArtists.map((artist) => (
                  <ArtistItem
                    key={artist.artistId}
                    onClick={() => handleArtistSelect(artist)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      {artist.thumbnail && (
                        <img
                          src={artist.thumbnail}
                          alt={artist.artistName}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: '12px'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <ArtistName>{artist.artistName}</ArtistName>
                    </div>
                    <ArtistType>{artist.actType}</ArtistType>
                  </ArtistItem>
                ))
              ) : searchTerm && !isLoadingArtists ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Nenhum artista encontrado
                </div>
              ) : null}
            </ArtistList>
          </ArtistModalContent>
        </ArtistModalOverlay>
      )}

      {showSupportingArtistModal && (
        <ArtistModalOverlay onClick={() => setShowSupportingArtistModal(false)}>
          <ArtistModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ArtistModalHeader>
              <ArtistModalTitle>Selecionar Apoios ao Artista</ArtistModalTitle>
              <ArtistModalClose onClick={() => setShowSupportingArtistModal(false)}>
                <Icon
                  width={30}
                  height={30}
                  iconName={'x'}
                  fill={'#000'}
                  classes="add-account-chevron"
                />
              </ArtistModalClose>
            </ArtistModalHeader>

            <ArtistSearchInput
              type="text"
              placeholder="Buscar artistas de apoio..."
              value={supportingSearchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSupportingSearchTerm(e.target.value)}
            />

            <ArtistList>
              {isLoadingSupportingArtists ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Buscando artistas de apoio...
                </div>
              ) : filteredSupportingArtists.length > 0 ? (
                filteredSupportingArtists.map((artist) => (
                  <ArtistItem
                    key={artist.artistId}
                    onClick={() => handleSupportingArtistSelect(artist)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      {artist.thumbnail && (
                        <img
                          src={artist.thumbnail}
                          alt={artist.artistName}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: '12px'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <ArtistName>{artist.artistName}</ArtistName>
                    </div>
                    <ArtistType>{artist.actType}</ArtistType>
                  </ArtistItem>
                ))
              ) : supportingSearchTerm && !isLoadingSupportingArtists ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Nenhum artista de apoio encontrado
                </div>
              ) : null}
            </ArtistList>
          </ArtistModalContent>
        </ArtistModalOverlay>
      )}
    </>
  )
}